import { Client } from "@stomp/stompjs";
import apiService from "../Simplify360Chat/Services/APIService";
import { s360ConfigService } from "../Simplify360Chat/Services/S360Config";
import endpoints from "../utils/Constants/endpoints";
import {
  ConvoType,
  MessageType,
} from "../Simplify360Chat/Conversation/MessageBoxTypes/Message";

const activeClients = new Map();
const manualReconnectRequests = new Set();
const AUTO_RECONNECT_EVENT = "chatWSReconnected";

export const initChatWSConnection = (chatWorkitemId, resume) => {
  const effectiveChatWorkitemId =
    chatWorkitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
    );

  if (!effectiveChatWorkitemId) {
    return null;
  }

  const existingClient = activeClients.get(effectiveChatWorkitemId);
  if (existingClient && existingClient.connected) {
    return existingClient;
  }

  if (existingClient) {
    activeClients.delete(effectiveChatWorkitemId);
  }

  let timerKeepAlive;
  let keepAlive403Count = 0;
  let keepAliveAborted = false;
  let messageTypingTimeout = null;
  // Handlers are kept as refs to properly remove them and avoid leaks
  let visibilityChangeHandler = null;
  let pageHideHandler = null;
  let appOpenChangedHandler = null;
  let externalControlHandler = null;
  let isAppOpenState = false; // only start when UI marks open
  let externallyPaused = false; // tracks whether parent SDK paused keepalive (tab/app blur)
  const MIN_RECONNECT_DELAY = 1000;
  const MAX_RECONNECT_DELAY = 30000;
  let reconnectAttempts = 0;

  const computeReconnectDelay = (attempt = 0) => {
    if (attempt <= 0) {
      return MIN_RECONNECT_DELAY;
    }
    const exponentialDelay =
      MIN_RECONNECT_DELAY * Math.pow(2, Math.max(attempt - 1, 0));
    return Math.min(exponentialDelay, MAX_RECONNECT_DELAY);
  };

  const handleKeepAliveError = (err) => {
    const status = err?.response?.status;
    if (status === 403) {
      apiService.refreshAuthToken();
      keepAlive403Count += 1;
      if (keepAlive403Count >= 2) {
        keepAliveAborted = true;
        stopKeepAlive();
      }
    } else {
      keepAlive403Count = 0;
    }
  };

  const startKeepAlive = () => {
    // If timer is already running, do nothing (it's recurring)
    if (timerKeepAlive) {
      return;
    }

    // Checks for abort conditions
    if (
      document.visibilityState !== "visible" ||
      !isAppOpenState ||
      keepAliveAborted ||
      externallyPaused
    ) {
      return;
    }

    // Ensure only the latest active workitem runs keepalive
    const currentStoredWorkitemId = localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
    );
    if (
      currentStoredWorkitemId &&
      currentStoredWorkitemId !== effectiveChatWorkitemId
    ) {
      // This is not the latest workitem, don't start keepalive
      return;
    }


    // IMMEDIATE call on start (unless aborted)
    if (!keepAliveAborted) {
      apiService
        .keepAlive(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId,
        )
        .then(() => {
          keepAlive403Count = 0;
        })
        .catch(handleKeepAliveError);
    }

    // START INTERVAL for recurring calls
    timerKeepAlive = setInterval(() => {
      if (keepAliveAborted) {
        stopKeepAlive();
        return;
      }
      // Stop keepalive if this is no longer the latest active workitem
      const currentStoredWorkitemId = localStorage.getItem(
        `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
      );
      if (
        currentStoredWorkitemId &&
        currentStoredWorkitemId !== effectiveChatWorkitemId
      ) {
        stopKeepAlive();
        return;
      }
      apiService
        .keepAlive(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId,
        )
        .then(() => {
          keepAlive403Count = 0;
        })
        .catch(handleKeepAliveError);
    }, 10000);
  };

  const stopKeepAlive = () => {
    if (timerKeepAlive) {
      clearInterval(timerKeepAlive);
      timerKeepAlive = null;
    }
  };

  const attachVisibilityHandlers = () => {
    if (visibilityChangeHandler || pageHideHandler) return;
    visibilityChangeHandler = () => {
      if (document.visibilityState === "hidden") {
        stopKeepAlive();
      } else {
        // Only restart if app is open, not aborted, AND not externally paused
        // The external control from the parent SDK tracks whether the user
        // is actually in the browser/tab. We defer to that signal.
        if (isAppOpenState && !keepAliveAborted && !externallyPaused) {
          startKeepAlive();
        } else {
        }
      }
    };
    pageHideHandler = () => {
      stopKeepAlive();
    };
    document.addEventListener("visibilitychange", visibilityChangeHandler);
    window.addEventListener("pagehide", pageHideHandler);
  };

  const detachVisibilityHandlers = () => {
    if (visibilityChangeHandler) {
      document.removeEventListener("visibilitychange", visibilityChangeHandler);
      visibilityChangeHandler = null;
    }
    if (pageHideHandler) {
      window.removeEventListener("pagehide", pageHideHandler);
      pageHideHandler = null;
    }
  };

  const attachAppOpenHandler = () => {
    if (appOpenChangedHandler) return;
    appOpenChangedHandler = (e) => {
      const next = !!e?.detail?.isAppOpen;
      isAppOpenState = next;
      if (!isAppOpenState) {
        stopKeepAlive();
      } else {
        if (!keepAliveAborted && !externallyPaused) {
          startKeepAlive();
        } else {
        }
      }
    };
    window.addEventListener("S360AppOpenChanged", appOpenChangedHandler);
  };

  // Add new event listener for stopKeepAlive
  let stopKeepAliveHandler = null;

  const attachStopKeepAliveHandler = () => {
    if (stopKeepAliveHandler) return;
    const workitemId =
      effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId;
    stopKeepAliveHandler = (e) => {
      keepAliveAborted = true;
      // Call stopKeepAlive when event is received
      stopKeepAlive();
      detachAppOpenHandler();
      detachVisibilityHandlers();
      detachStopKeepAliveHandler();
      detachExternalControlHandler();

      // Clean up the client for this workitem
      if (client && client.connected) {
        client.deactivate();
      }
      activeClients.delete(workitemId);
    };
    window.addEventListener(
      `stopKeepAlive_${workitemId}`,
      stopKeepAliveHandler,
    );
  };

  const detachStopKeepAliveHandler = () => {
    if (stopKeepAliveHandler) {
      const workitemId =
        effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId;
      window.removeEventListener(
        `stopKeepAlive_${workitemId}`,
        stopKeepAliveHandler,
      );
      stopKeepAliveHandler = null;
    }
  };

  const detachAppOpenHandler = () => {
    if (appOpenChangedHandler) {
      window.removeEventListener("S360AppOpenChanged", appOpenChangedHandler);
      appOpenChangedHandler = null;
    }
  };

  const attachExternalControlHandler = () => {
    if (externalControlHandler) return;
    externalControlHandler = (event) => {
      try {
        const trustedParentOrigin = new URL(
          s360ConfigService.sourceOrigin,
        ).origin;
        if (
          event.source !== window.parent ||
          event.origin !== trustedParentOrigin
        ) {
          return;
        }

        const data =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;

        if (
          data &&
          (data.type === "control-keepalive" ||
            data.action === "control-keepalive")
        ) {
          const payload = data.data || data; // Handle both wrapped and unwrapped

          if (payload.status === "pause") {
            externallyPaused = true;
            stopKeepAlive();
          } else if (payload.status === "resume") {
            externallyPaused = false;

            // Don't resume if keepalive was aborted (workitem terminated/dispositioned)
            if (keepAliveAborted) {
              return;
            }
            if (!isAppOpenState) {
              return;
            }

            // Ensure this is still the latest active workitem before resuming
            const currentStoredWorkitemId = localStorage.getItem(
              `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
            );
            if (
              currentStoredWorkitemId &&
              currentStoredWorkitemId !== effectiveChatWorkitemId
            ) {
              // Not the latest workitem — don't resume keepalive for this old one
              return;
            }

            // Verify workitem is still active (not terminated/dispositioned) before resuming
            apiService
              .getChatStaleStatus(
                effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId,
              )
              .then((res) => {
                const state = res?.data?.state;
                if (state === "terminated" || state === "dispositioned") {
                  // Workitem ended while user was away — abort keepalive permanently
                  keepAliveAborted = true;
                  stopKeepAlive();
                } else {
                  // Workitem still active — safe to resume
                  if (!externallyPaused && !keepAliveAborted) {
                    startKeepAlive();
                  } else {
                  }
                }
              })
              .catch((err) => {
                // On error (e.g. network), optimistically resume keepalive
                // The interval's own error handling will catch persistent issues
                if (!externallyPaused && !keepAliveAborted) {
                  startKeepAlive();
                }
              });
          }
        }
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener("message", externalControlHandler);
  };

  const detachExternalControlHandler = () => {
    if (externalControlHandler) {
      window.removeEventListener("message", externalControlHandler);
      externalControlHandler = null;
    }
  };

  const client = new Client({
    brokerURL: endpoints.WS_URL.replace(
      ":location",
      s360ConfigService.config.location.split("https://")[1],
    ),
    connectionTimeout: 10000,
    reconnectDelay: computeReconnectDelay(),
    heartbeatIncoming: 10000,
    heartbeatOutgoing: 10000,
    debug: (msg) => console.debug(`[STOMP][${effectiveChatWorkitemId}] ${msg}`),
    beforeConnect: () => {
      if (keepAliveAborted) {
        return;
      }
      if (reconnectAttempts > 0) {
        client.reconnectDelay = computeReconnectDelay(reconnectAttempts);
      }
    },
    onConnect: () => {
      const hadManualRequest = manualReconnectRequests.has(
        effectiveChatWorkitemId,
      );
      const autoReconnect = reconnectAttempts > 0 && !hadManualRequest;

      if (hadManualRequest) {
        manualReconnectRequests.delete(effectiveChatWorkitemId);
      }

      if (autoReconnect) {
        window.dispatchEvent(
          new CustomEvent(AUTO_RECONNECT_EVENT, {
            detail: {
              workitemId: effectiveChatWorkitemId,
              attempts: reconnectAttempts,
              timestamp: Date.now(),
            },
          }),
        );
      }

      reconnectAttempts = 0;
      client.reconnectDelay = computeReconnectDelay();
      // Store the client for this workitem
      activeClients.set(effectiveChatWorkitemId, client);

      if (!s360ConfigService.config.preview) {
        if (s360ConfigService.config.isNewConversation) {
          // Attach handlers and kick off keep-alive only when tab is visible
          attachVisibilityHandlers();
          attachAppOpenHandler();
          attachStopKeepAliveHandler();
          attachExternalControlHandler();
          // Sync initial state if UI updated it before we attached
          try {
            if (typeof window !== "undefined") {
              isAppOpenState = !!window.__S360AppOpenState;
            }
          } catch (_) {}
          startKeepAlive();
        }
        if (!resume) return;

        apiService.triggerChatRequest(
          effectiveChatWorkitemId || s360ConfigService.resumedWorkitemId,
        );
      }

      client.subscribe(
        endpoints.WS_CHAT_SUB.replace(
          ":chatWorkitemId",
          effectiveChatWorkitemId,
        ).replace(":tenant", s360ConfigService.config.tenantId),
        (message) => {
          const body = JSON.parse(message.body);

          switch (body.name) {
            case "WorkitemAssignUserNotification":
              window.dispatchEvent(
                new CustomEvent("agentConnected", {
                  detail: { workitemId: effectiveChatWorkitemId },
                }),
              );
              break;
            case "WorkitemTerminatedNotification":
              {
                keepAliveAborted = true;
                stopKeepAlive();
                detachVisibilityHandlers();
                detachAppOpenHandler();
                detachExternalControlHandler();
                detachStopKeepAliveHandler();
                client.deactivate();

                // Clean up this specific workitem's client
                activeClients.delete(effectiveChatWorkitemId);

                const messageEvent = new CustomEvent(body.name, {
                  detail: body,
                });
                window.dispatchEvent(messageEvent);
              }
              break;
            case "MessageNotification":
              //HANDLING SOCKET EVENT ON PREVIEW - STEP 1
              const messageObject = body.engagementData;
              // if (messageObject?.fromUser) {
              //   // if (s360ConfigService) {
              //   //   if (s360ConfigService && s360ConfigService.config) {
              //   //     s360ConfigService.config.nickName = body.message?.fromId;
              //   //   }
              //   // }

              //   // if (
              //   //   ![null, undefined].includes(body) &&
              //   //   ![null, undefined].includes(body.message) &&
              //   //   ![null, undefined].includes(body.message.fromId)
              //   // ) {
              //   //   messageObject.fromUser.messageFromId = body.message.fromId;
              //   // } else {
              //   //   messageObject.fromUser.messageFromId = "";
              //   // }
              // }
              const messageEvent = new CustomEvent(body.name, {
                detail: messageObject,
              });
              window.dispatchEvent(messageEvent);
              break;
            case "EngagementDataStatusUpdateNotification":
              const messageStatusEvent = new CustomEvent(body.name, {
                detail: body,
              });
              window.dispatchEvent(messageStatusEvent);
              break;
            case "MessageTypingNotification":
              const messagTypingeEvent = new CustomEvent(body.name, {
                detail: body,
              });
              clearTimeout(messageTypingTimeout);
              window.dispatchEvent(messagTypingeEvent);
              messageTypingTimeout = setTimeout(() => {
                const messagTypingeEvent = new CustomEvent(body.name, {
                  detail: null,
                });
                window.dispatchEvent(messagTypingeEvent);
              }, 2000);
              break;
            case "MessageStatusNotification": // code handling pending
              return;
            case "WebSocketSubscriptionStatusNotification":
              if (
                s360ConfigService.config.preview &&
                !s360ConfigService.config.previewInitialized
              ) {
                apiService
                  .initPreview()
                  .then((res) => {
                    s360ConfigService.config.previewInitialized = true;
                  })
                  .catch((err) => {});
              }
          }
        },
      );
    },
    onDisconnect: () => {
      sessionStorage?.removeItem("subscribed");
      stopKeepAlive();
      detachVisibilityHandlers();
      detachAppOpenHandler();
      detachStopKeepAliveHandler();
      detachExternalControlHandler();
      if (messageTypingTimeout) {
        clearTimeout(messageTypingTimeout);
        messageTypingTimeout = null;
      }

      // Clean up this workitem's client on disconnect
      activeClients.delete(effectiveChatWorkitemId);
      manualReconnectRequests.delete(effectiveChatWorkitemId);
    },
    onWebSocketClose: (event) => {
      if (keepAliveAborted) {
        return;
      }
      reconnectAttempts += 1;
      const delay = computeReconnectDelay(reconnectAttempts);
      client.reconnectDelay = delay;
      console.warn(
        `WebSocket closed for workitem ${effectiveChatWorkitemId}. ` +
          `Retrying in ${delay}ms (attempt ${reconnectAttempts}).`,
        event?.reason,
      );
    },
    onWebSocketError: (event) => {
      console.error(
        `WebSocket error for workitem ${effectiveChatWorkitemId}.`,
        event,
      );
    },
  });

  client.activate();
  return client;
};

// Utility function to check if a workitem has an active connection
export const hasActiveConnection = (workitemId) => {
  const client = activeClients.get(workitemId);
  return client && client.connected;
};

// Utility function to disconnect a specific workitem
export const disconnectWorkitem = (workitemId) => {
  const client = activeClients.get(workitemId);
  if (client && client.connected) {
    client.deactivate();
  }
  activeClients.delete(workitemId);
  manualReconnectRequests.delete(workitemId);
};

// Safe reconnection utility - prevents multiple connections and preserves existing ones
export const safeReconnectWebSocket = (chatWorkitemId, resume = true) => {
  const effectiveChatWorkitemId =
    chatWorkitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
    );

  if (!effectiveChatWorkitemId) {
    console.warn("Cannot reconnect WebSocket: No workitem ID available");
    return null;
  }

  manualReconnectRequests.add(effectiveChatWorkitemId);

  // CRITICAL: Check if already connected - NEVER create duplicate connections
  const existingClient = activeClients.get(effectiveChatWorkitemId);
  if (existingClient && existingClient.connected) {
    manualReconnectRequests.delete(effectiveChatWorkitemId);
    return existingClient; // Return existing connection - DO NOT create new one
  }

  // Check if client exists but is disconnected
  if (existingClient && !existingClient.connected) {
    // Clean up disconnected client
    try {
      existingClient.deactivate();
    } catch (error) {
      console.warn("Error deactivating disconnected client:", error);
    }
    activeClients.delete(effectiveChatWorkitemId);
  }

  const newClient = initChatWSConnection(chatWorkitemId, resume);

  if (!newClient) {
    manualReconnectRequests.delete(effectiveChatWorkitemId);
  }

  return newClient;
};

// Enhanced connection status checker with detailed logging
export const getConnectionStatus = (workitemId) => {
  const effectiveWorkitemId =
    workitemId ||
    localStorage.getItem(
      `${s360ConfigService?.config?.widgetconfigId}_workitemId`,
    );

  const client = activeClients.get(effectiveWorkitemId);

  const status = {
    workitemId: effectiveWorkitemId,
    hasClient: !!client,
    isConnected: client && client.connected,
    clientState: client
      ? client.connected
        ? "connected"
        : "disconnected"
      : "none",
    timestamp: new Date().toISOString(),
  };

  return status;
};
